// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { FAQPageJsonLd, NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import styled from '@emotion/styled'

// Misc
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'

import { HeroSection, Title as HeroTitle, Description as HeroDescription } from '../components/Hero'
import PrimaryAction from '../components/PrimaryAction'
import { CenteredSection, Title as CenteredTitle } from '../components/CenteredSection'

import ColoredBackground from '../components/ColoredBackground'
import InlineLink from '../components/InlineLink'
import Highlight from '../components/Highlight'
import FeatureGrid from '../components/FeatureGrid'
import BottomCta from '../components/BottomCta'

import PreviouslyGradeproofImg from '../../public/assets/previously-gradeproof.png'

import { defaultPageSpecificSEO, pageSpecificCtaLink } from '../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'gradeproof' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, gradeproof: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & Styles
 */

const HERO_BACKGROUND = 'yellow'

const CenteredHeroSection = styled(HeroSection)`
  text-align: center;
`

const Gradeproof: NextPage = () => {
  const router = useRouter()

  const { t } = useTranslation('gradeproof')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />
      <FAQPageJsonLd
        mainEntity={[
          {
            questionName: t('seoFaq.gradeproofUser.QUESTION'),
            acceptedAnswerText: t('seoFaq.gradeproofUser.ANSWER')
          }
        ]}
      />

      {/* Above the fold */}
      <Header
        t={tCommon}
        action={ctaAction}
        data-sticky={scrolledPassed}
        bgColor={HERO_BACKGROUND}
        dark
      />
      <ColoredBackground bgColor={HERO_BACKGROUND}>
        {/* Hero */}
        <CenteredHeroSection>
          <HeroTitle>{t('hero.TITLE')}</HeroTitle>
          <Image src={PreviouslyGradeproofImg} alt='Previously GradeProof' width={450} height={300} priority />
          <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
          <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
        </CenteredHeroSection>
      </ColoredBackground>

      {/* FAQ */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey={'faq.TITLE'}><Highlight color='green'/></Trans>
        </CenteredTitle>
        <FeatureGrid
          cols={2}
          descVisibleOnMobile
          list={[
            {
              title: t('faq.change.QUESTION'),
              description: <Trans t={t} i18nKey='faq.change.ANSWER'><InlineLink exthref='https://blog.outwrite.com/gradeproof-is-growing-up/'/></Trans>
            },
            {
              title: t('faq.same.QUESTION'),
              description: t('faq.same.ANSWER')
            },
            {
              title: t('faq.account.QUESTION'),
              description: <Trans t={t} i18nKey='faq.account.ANSWER'><InlineLink apphref='/auth/login'/></Trans>
            }
          ]}
        />
      </CenteredSection>

      {/* BottomCta */}
      <BottomCta action={ctaAction} {...t('BOTTOM_CTA')} bgColor='gray' />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Gradeproof
